/**
 * Calls `onChange` now and whenever an element's rendered content may have
 * changed size: the element or a child resizes, its DOM changes, or an image
 * inside it loads.
 *
 * @param element - Element to watch
 * @param onChange - Re-measures the element
 * @returns Stops watching
 */
export function observeContentChanges(
  element: HTMLElement,
  onChange: () => void,
): () => void {
  onChange();

  const resizeObserver =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onChange);
  resizeObserver?.observe(element);
  for (const child of element.children) resizeObserver?.observe(child);

  const mutationObserver = new MutationObserver(onChange);
  mutationObserver.observe(element, {
    childList: true,
    characterData: true,
    subtree: true,
  });
  element.addEventListener('load', onChange, true);

  return () => {
    resizeObserver?.disconnect();
    mutationObserver.disconnect();
    element.removeEventListener('load', onChange, true);
  };
}
